<template>
  <div class="alert alert-success border-success">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div class="mb-3">
      <div class="h4 fw-bold">Manage Resources</div>
      <div class="alert alert-light">
        Selected Question:
        <span class="fw-bold">{{ selectedQuestionText }} </span>
      </div>
      <div class="fst-italic">
        Add, edit, or delete resources for your survey.
      </div>
    </div>

    <div class="row mb-2 align-items-center">
      <div class="col-1 text-end fw-bold">#</div>
      <div class="col-3 fw-bold">Question</div>
      <div class="col-3 fw-bold">Resource Text</div>
      <div class="col-3 fw-bold">Resource URL</div>
      <div class="col-1 fw-bold text-end">Save</div>
      <div class="col-1 fw-bold text-end">Delete</div>
    </div>
    <div v-if="!isLoading">
      <div
        v-for="(resource, index) in filteredResources"
        :key="resource.opportunityForImprovementId"
      >
        <div class="row mb-2 align-items-center">
          <div class="col-12 col-md-1 text-end">
            <span class="badge bg-secondary px-3 py-2">{{ index + 1 }}</span>
          </div>
          <div class="col-12 col-md-3">
            <select
              class="form-control shadow-sm"
              v-model="resource.questionUUID"
            >
              <option
                v-for="question in questions"
                :value="question.uuid"
                :key="`${resource.opportunityForImprovementId}-${question.uuid}-${resource.opportunityForImprovementText}-${index}`"
              >
                {{ question.questionText }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <input
              type="text"
              ref="resourceInputs"
              :aria-label="resource.opportunityForImprovementText"
              class="form-control shadow-sm"
              v-model="resource.opportunityForImprovementText"
              placeholder="Enter Resource Text"
            />
          </div>
          <div class="col-12 col-md-3">
            <input
              type="text"
              :aria-label="resource.opportunityForImprovementUrl"
              class="form-control shadow-sm"
              v-model="resource.opportunityForImprovementUrl"
              placeholder="Enter Resource URL"
            />
          </div>
          <div class="col-12 col-md-1 text-end">
            <button
              class="btn btn-success fs-4 py-0 px-2"
              aria-label="Save"
              @click="saveResource(resource)"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title="Save Changes"
            >
              <i class="bi bi-box-arrow-down"></i>
            </button>
          </div>
          <div class="col-12 col-md-1 text-end">
            <button
              class="btn btn-danger fs-4 py-0 px-2"
              aria-label="Delete"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title="Delete"
              @click="removeResource(resource)"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isLoading">
      <div class="row mt-3">
        <div class="col-12 col-md-1"></div>
        <div class="col-12 col-md-3">
          <select
            class="form-control shadow-sm"
            v-model="newResourceQuestionUUID"
            placeholder="Select Question"
          >
            <option
              v-for="question in questions"
              :value="question.uuid"
              :key="`new-resource-${question.uuid}-${newResourceQuestionUUID.questionText}`"
            >
              {{ question.questionText }}
            </option>
          </select>
        </div>
        <div class="col-12 col-md-3">
          <input
            type="text"
            class="form-control shadow-sm"
            v-model="newResourceText"
            placeholder="Enter Resource Text"
          />
        </div>
        <div class="col-12 col-md-3">
          <input
            type="text"
            class="form-control shadow-sm"
            v-model="newResourceUrl"
            placeholder="Enter Resource URL"
          />
        </div>
        <div class="col-12 col-md-1 text-end">
          <button class="btn btn-success fs-4 py-0 px-2" @click="addResource">
            <i class="bi bi-plus"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { v4 as uuidv4 } from "uuid";

import ContentAPI from "@/helpers/ContentAPI";

export default {
  name: "AdminControlResources",
  mixins: [ContentAPI],
  props: {
    selectedQuestionUUID: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      newResourceText: "",
      newResourceUrl: "",
      newResourceQuestionUUID: this.selectedQuestionUUID,
      isLoading: false,
      page: "Sheet2"
    };
  },
  computed: {
    ...mapGetters(["getRecommendationData", "getQuestionData"]),
    resources() {
      return this.getRecommendationData;
    },
    questions() {
      return this.getQuestionData;
    },
    filteredResources() {
      if (this.selectedQuestionUUID) {
        return this.resources.filter(
          (resource) => resource.questionUUID === this.selectedQuestionUUID
        );
      }
      return this.resources.filter((resource) =>
        this.questions.some(
          (question) => question.uuid === resource.questionUUID
        )
      );
    },
    selectedQuestionText() {
      return (
        this.questions.find(
          (question) => question.uuid === this.selectedQuestionUUID
        )?.questionText || "No Question Selected"
      );
    }
  },
  methods: {
    ...mapActions(["fetchQuestionData", "fetchRecommendationData"]),
    addResource() {
      this.isLoading = true;
      if (
        this.newResourceQuestionUUID.trim() === "" ||
        this.newResourceText.trim() === "" ||
        this.newResourceUrl.trim() === ""
      ) {
        alert(
          "Please enter resource text, URL, and select a question before submitting."
        );
        this.isLoading = false;
        return;
      }
      const newUuid = uuidv4();
      const payloadParsed = {
        opportunity_for_improvement_text: this.newResourceText,
        opportunity_for_improvement_url: this.newResourceUrl,
        question_uuid: this.newResourceQuestionUUID,
        opportunity_for_improvement_id: newUuid
      };
      const page = this.page;
      this.createContentEntry(payloadParsed, page, () => {
        this.isLoading = false;
        this.newResourceText = "";
        this.newResourceUrl = "";
        this.newResourceQuestionUUID = this.selectedQuestionUUID;
        this.fetchQuestionData();
        this.fetchRecommendationData();
      });
    },
    saveResource(resource) {
      if (
        resource.opportunityForImprovementText.trim() === "" ||
        resource.opportunityForImprovementUrl.trim() === "" ||
        resource.questionUUID.trim() === ""
      ) {
        alert(
          "Please enter resource text, URL, and select a question before submitting."
        );
        return;
      }
      this.isLoading = true;

      const payloadParsed = {
        opportunity_for_improvement_text:
          resource.opportunityForImprovementText,
        opportunity_for_improvement_url: resource.opportunityForImprovementUrl,
        question_uuid: resource.questionUUID,
        opportunity_for_improvement_id: resource.opportunityForImprovementId
      };

      // Update resource in the backend (Uncomment below lines when integrating)
      const page = this.page;
      this.updateContentEntry(payloadParsed, page, () => {
        this.fetchQuestionData();
        this.fetchRecommendationData();
        this.isLoading = false;
      });
    },
    removeResource(payloadFromForm) {
      this.isLoading = true;
      const payload = {
        uid_key: "opportunity_for_improvement_text",
        uid_value: payloadFromForm.opportunityForImprovementText
      };
      const page = this.page;
      this.deleteContentEntry(payload, page, () => {
        this.isLoading = false;
        this.fetchQuestionData();
        this.fetchRecommendationData();
      });
    }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
</style>
